import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Save, MessageSquare, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import RandomNextToggle from './shared/RandomNextToggle';

interface MessageConfigProps {
    node: FlowNode;
    onClose: () => void;
}

export default function MessageConfig({ node, onClose }: MessageConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [message, setMessage] = useState(node.data.config?.message || '');
    const [typingSeconds, setTypingSeconds] = useState(node.data.config?.delay_seconds || 0);
    const [isRandom, setIsRandom] = useState<boolean>(node.data.is_random ?? false);

    const saveMutation = useMutation({
        mutationFn: () => updateNode(node.data.id, {
            node_name: nodeName,
            is_random: isRandom,
            config: { message, delay_seconds: typingSeconds || undefined }
        }),
        onSuccess: () => {
            toast({ title: 'Mensagem salva!', description: 'Configurações salvas com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const characterCount = message.length;
    const maxCharacters = 2000;

    return (
        <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Nome */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Nome da Mensagem</Label>
                    <Input
                        value={nodeName}
                        onChange={(e) => setNodeName(e.target.value)}
                        placeholder="Ex: Boas-vindas"
                        className="h-10"
                    />
                </div>

                {/* Conteúdo da Mensagem */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-gray-700">Conteúdo da Mensagem</Label>
                        <span className={`text-xs ${characterCount > maxCharacters ? 'text-red-600' : 'text-gray-500'}`}>
                            {characterCount} / {maxCharacters}
                        </span>
                    </div>
                    <Textarea
                        placeholder="Digite sua mensagem aqui...&#10;&#10;Você pode usar múltiplas linhas."
                        rows={8}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="resize-none font-normal"
                        maxLength={maxCharacters}
                    />
                    <p className="text-xs text-gray-500">
                        💡 Use quebras de linha para organizar melhor sua mensagem
                    </p>
                </div>

                {/* Preview do Messenger */}
                {message && (
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Preview do Messenger</Label>
                        <div className="bg-gradient-to-b from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                                    <MessageSquare className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-gray-900">Sua Página</div>
                                    <div className="text-xs text-gray-500">Online</div>
                                </div>
                            </div>

                            <div className="flex justify-start">
                                <div className="max-w-[85%]">
                                    <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm border border-gray-200">
                                        <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
                                            {message}
                                        </p>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 ml-3">
                                        Agora
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!message && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-xs text-blue-900">
                            💡 <strong>Dica:</strong> Escreva uma mensagem clara e objetiva. Ela será enviada automaticamente pelo Messenger quando o usuário atingir este ponto do fluxo.
                        </p>
                    </div>
                )}

                {/* Digitando */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <MessageCircle className="w-3.5 h-3.5" />
                        Digitando... (segundos)
                    </Label>
                    <Input
                        type="number"
                        min={0}
                        max={30}
                        value={typingSeconds}
                        onChange={(e) => setTypingSeconds(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
                        placeholder="0"
                        className="h-10 w-32"
                    />
                    <p className="text-xs text-gray-500">
                        Mostra "digitando..." por X segundos antes de enviar. 0 = desativado.
                    </p>
                </div>

                <RandomNextToggle
                    isRandom={isRandom}
                    onChange={setIsRandom}
                    disabled={saveMutation.isPending}
                />

                {/* Variáveis disponíveis */}
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <p className="text-xs font-semibold text-cyan-900 mb-2">📝 Variáveis Disponíveis</p>
                    <div className="space-y-1.5">
                        <div className="flex items-start gap-2">
                            <code className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded font-mono">
                                {'{{$first_name}}'}
                            </code>
                            <span className="text-xs text-cyan-800">Primeiro nome do usuário</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <code className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded font-mono">
                                {'{{$last_name}}'}
                            </code>
                            <span className="text-xs text-cyan-800">Sobrenome do usuário</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <code className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded font-mono">
                                {'{{$full_name}}'}
                            </code>
                            <span className="text-xs text-cyan-800">Nome completo do usuário</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="border-t bg-white p-6 flex-shrink-0">
                <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending || !message.trim() || characterCount > maxCharacters}
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saveMutation.isPending ? 'Salvando...' : 'Salvar Mensagem'}
                </Button>
            </div>
        </>
    );
}